'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdAttachMoney, MdInfo } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createCoparticipationRule,
    updateCoparticipationRule,
    getCoparticipationRule,
    CreateCoparticipationRuleData,
    UpdateCoparticipationRuleData,
    CoparticipationType,
    COPARTICIPATION_TYPE_LABELS,
    COPARTICIPATION_TYPE_DESCRIPTIONS,
} from '@/lib/api/billing/coparticipation-api'
import { getHealthPlans, HealthPlan } from '@/lib/api/medical/health-plan-api'
import { format } from 'date-fns'

interface FormData {
    healthPlanId: string
    name: string
    type: CoparticipationType
    percentageValue: string
    fixedValue: string
    ceilingValue: string
    deductibleValue: string
    procedureCodes: string
    orderCategories: string
    appliesToConsultation: boolean
    appliesToExam: boolean
    appliesToOrder: boolean
    appliesToHospitalization: boolean
    validFrom: string
    validUntil: string
    active: boolean
}

export default function CoparticipationFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([])

    const [formData, setFormData] = useState<FormData>({
        healthPlanId: '',
        name: '',
        type: 'PERCENTAGE',
        percentageValue: '',
        fixedValue: '',
        ceilingValue: '',
        deductibleValue: '',
        procedureCodes: '',
        orderCategories: '',
        appliesToConsultation: true,
        appliesToExam: true,
        appliesToOrder: true,
        appliesToHospitalization: false,
        validFrom: '',
        validUntil: '',
        active: true,
    })

    useEffect(() => {
        if (user?.companyId) {
            loadHealthPlans()
            if (isEditing && id) {
                loadRule()
            }
        }
    }, [id, isEditing, user?.companyId])

    const loadHealthPlans = async () => {
        if (!user?.companyId) return
        try {
            const response = await getHealthPlans(user.organizationId)
            if (response.success) {
                setHealthPlans(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar convênios:', error)
        }
    }

    const loadRule = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getCoparticipationRule(parseInt(id, 10))
            if (response.success && response.result) {
                const rule = response.result
                setFormData({
                    healthPlanId: rule.healthPlanId?.toString() || '',
                    name: rule.name || '',
                    type: rule.type || 'PERCENTAGE',
                    percentageValue: rule.percentageValue?.toString() || '',
                    fixedValue: rule.fixedValue?.toString() || '',
                    ceilingValue: rule.ceilingValue?.toString() || '',
                    deductibleValue: rule.deductibleValue?.toString() || '',
                    procedureCodes: rule.procedureCodes?.join(', ') || '',
                    orderCategories:
                        rule.orderCategories?.join(', ') || '',
                    appliesToConsultation: rule.appliesToConsultation ?? true,
                    appliesToExam: rule.appliesToExam ?? true,
                    appliesToOrder: rule.appliesToOrder ?? true,
                    appliesToHospitalization:
                        rule.appliesToHospitalization ?? false,
                    validFrom: rule.validFrom
                        ? format(new Date(rule.validFrom), 'yyyy-MM-dd')
                        : '',
                    validUntil: rule.validUntil
                        ? format(new Date(rule.validUntil), 'yyyy-MM-dd')
                        : '',
                    active: rule.active ?? true,
                })
            } else {
                toast.error(response.error || 'Erro ao carregar regra')
                navigate('/billing/coparticipação')
            }
        } catch (error) {
            toast.error('Erro ao carregar regra')
            navigate('/billing/coparticipação')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const validateForm = (): boolean => {
        if (!formData.healthPlanId) {
            toast.error('Selecione um convênio')
            return false
        }
        if (!formData.name.trim()) {
            toast.error('Informe o nome da regra')
            return false
        }

        switch (formData.type) {
            case 'PERCENTAGE':
                if (
                    !formData.percentageValue ||
                    parseFloat(formData.percentageValue) <= 0
                ) {
                    toast.error('Informe o percentual')
                    return false
                }
                break
            case 'FIXED_VALUE':
                if (
                    !formData.fixedValue ||
                    parseFloat(formData.fixedValue) <= 0
                ) {
                    toast.error('Informe o valor fixo')
                    return false
                }
                break
            case 'MIXED':
                if (
                    !formData.percentageValue ||
                    !formData.fixedValue ||
                    parseFloat(formData.percentageValue) <= 0 ||
                    parseFloat(formData.fixedValue) <= 0
                ) {
                    toast.error('Informe o percentual e o valor fixo')
                    return false
                }
                break
            case 'CEILING':
                if (
                    !formData.ceilingValue ||
                    parseFloat(formData.ceilingValue) <= 0
                ) {
                    toast.error('Informe o valor do teto')
                    return false
                }
                break
            case 'DEDUCTIBLE':
                if (
                    !formData.deductibleValue ||
                    parseFloat(formData.deductibleValue) <= 0
                ) {
                    toast.error('Informe o valor da franquia')
                    return false
                }
                break
        }

        if (
            !formData.appliesToConsultation &&
            !formData.appliesToExam &&
            !formData.appliesToOrder &&
            !formData.appliesToHospitalization
        ) {
            toast.error('Selecione pelo menos um tipo de servico')
            return false
        }

        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            const procedureCodes = formData.procedureCodes
                .split(',')
                .map(c => c.trim())
                .filter(c => c)
            const orderCategories = formData.orderCategories
                .split(',')
                .map(c => c.trim())
                .filter(c => c)

            if (isEditing && id) {
                const updateData: UpdateCoparticipationRuleData = {
                    name: formData.name.trim(),
                    type: formData.type,
                    percentageValue: formData.percentageValue
                        ? parseFloat(formData.percentageValue)
                        : undefined,
                    fixedValue: formData.fixedValue
                        ? parseFloat(formData.fixedValue)
                        : undefined,
                    ceilingValue: formData.ceilingValue
                        ? parseFloat(formData.ceilingValue)
                        : undefined,
                    deductibleValue: formData.deductibleValue
                        ? parseFloat(formData.deductibleValue)
                        : undefined,
                    procedureCodes:
                        procedureCodes.length > 0 ? procedureCodes : undefined,
                    orderCategories:
                        orderCategories.length > 0
                            ? orderCategories
                            : undefined,
                    appliesToConsultation: formData.appliesToConsultation,
                    appliesToExam: formData.appliesToExam,
                    appliesToOrder: formData.appliesToOrder,
                    appliesToHospitalization: formData.appliesToHospitalization,
                    validFrom: formData.validFrom || undefined,
                    validUntil: formData.validUntil || undefined,
                    active: formData.active,
                }

                const response = await updateCoparticipationRule(
                    parseInt(id, 10),
                    updateData
                )
                if (response.success) {
                    toast.success('Regra atualizada com sucesso!')
                    navigate('/billing/coparticipação')
                } else {
                    toast.error(response.error || 'Erro ao atualizar regra')
                }
            } else {
                const createData: CreateCoparticipationRuleData = {
                    healthPlanId: parseInt(formData.healthPlanId, 10),
                    name: formData.name.trim(),
                    type: formData.type,
                    percentageValue: formData.percentageValue
                        ? parseFloat(formData.percentageValue)
                        : undefined,
                    fixedValue: formData.fixedValue
                        ? parseFloat(formData.fixedValue)
                        : undefined,
                    ceilingValue: formData.ceilingValue
                        ? parseFloat(formData.ceilingValue)
                        : undefined,
                    deductibleValue: formData.deductibleValue
                        ? parseFloat(formData.deductibleValue)
                        : undefined,
                    procedureCodes:
                        procedureCodes.length > 0 ? procedureCodes : undefined,
                    orderCategories:
                        orderCategories.length > 0
                            ? orderCategories
                            : undefined,
                    appliesToConsultation: formData.appliesToConsultation,
                    appliesToExam: formData.appliesToExam,
                    appliesToOrder: formData.appliesToOrder,
                    appliesToHospitalization: formData.appliesToHospitalization,
                    validFrom: formData.validFrom || undefined,
                    validUntil: formData.validUntil || undefined,
                }

                const response = await createCoparticipationRule(createData)
                if (response.success) {
                    toast.success('Regra criada com sucesso!')
                    navigate('/billing/coparticipação')
                } else {
                    toast.error(response.error || 'Erro ao criar regra')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar regra')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados da regra...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/billing/coparticipação')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <MdAttachMoney className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar Regra de Coparticipação'
                                : 'Nova Regra de Coparticipação'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações da regra'
                                : 'Configure uma nova regra de coparticipação'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Basic Info */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações Básicas
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Convênio{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.healthPlanId}
                                    onChange={e =>
                                        updateFormField(
                                            'healthPlanId',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                >
                                    <option value="">Selecione...</option>
                                    {healthPlans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome da Regra{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: Coparticipação 20% Consultas"
                                    value={formData.name}
                                    onChange={e =>
                                        updateFormField('name', e.target.value)
                                    }
                                    required
                                />
                            </div>
                        </div>

                        {isEditing && (
                            <div className="form-control mt-4">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-success"
                                        checked={formData.active}
                                        onChange={e =>
                                            updateFormField(
                                                'active',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Regra Ativa
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                {/* Type and Values */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Tipo e Valores
                        </h2>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo de Coparticipação{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.type}
                                onChange={e =>
                                    updateFormField(
                                        'type',
                                        e.target.value as CoparticipationType
                                    )
                                }
                            >
                                {(
                                    Object.keys(
                                        COPARTICIPATION_TYPE_LABELS
                                    ) as CoparticipationType[]
                                ).map(type => (
                                    <option key={type} value={type}>
                                        {COPARTICIPATION_TYPE_LABELS[type]}
                                    </option>
                                ))}
                            </select>
                            <div className="flex items-start gap-2 mt-2 p-3 bg-info/10 rounded-lg">
                                <MdInfo className="text-info text-lg flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-base-content/70">
                                    {
                                        COPARTICIPATION_TYPE_DESCRIPTIONS[
                                        formData.type
                                        ]
                                    }
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            {(formData.type === 'PERCENTAGE' ||
                                formData.type === 'MIXED') && (
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Percentual (%){' '}
                                                <span className="text-error">*</span>
                                            </span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            className="input input-bordered w-full"
                                            placeholder="Ex: 20"
                                            value={formData.percentageValue}
                                            onChange={e =>
                                                updateFormField(
                                                    'percentageValue',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                )}

                            {(formData.type === 'FIXED_VALUE' ||
                                formData.type === 'MIXED') && (
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Valor Fixo (R$){' '}
                                                <span className="text-error">*</span>
                                            </span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            className="input input-bordered w-full"
                                            placeholder="Ex: 50.00"
                                            value={formData.fixedValue}
                                            onChange={e =>
                                                updateFormField(
                                                    'fixedValue',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                )}

                            {formData.type === 'CEILING' && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Valor do Teto (R$){' '}
                                            <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: 200.00"
                                        value={formData.ceilingValue}
                                        onChange={e =>
                                            updateFormField(
                                                'ceilingValue',
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            )}

                            {formData.type === 'DEDUCTIBLE' && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Valor da Franquia (R$){' '}
                                            <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: 100.00"
                                        value={formData.deductibleValue}
                                        onChange={e =>
                                            updateFormField(
                                                'deductibleValue',
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Application */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Aplicação</h2>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <label className="label cursor-pointer justify-start gap-3 p-3 rounded-lg bg-base-200/50 hover:bg-base-200">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.appliesToConsultation}
                                    onChange={e =>
                                        updateFormField(
                                            'appliesToConsultation',
                                            e.target.checked
                                        )
                                    }
                                />
                                <span className="label-text">Consultas</span>
                            </label>

                            <label className="label cursor-pointer justify-start gap-3 p-3 rounded-lg bg-base-200/50 hover:bg-base-200">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.appliesToExam}
                                    onChange={e =>
                                        updateFormField(
                                            'appliesToExam',
                                            e.target.checked
                                        )
                                    }
                                />
                                <span className="label-text">Exames</span>
                            </label>

                            <label className="label cursor-pointer justify-start gap-3 p-3 rounded-lg bg-base-200/50 hover:bg-base-200">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.appliesToOrder}
                                    onChange={e =>
                                        updateFormField(
                                            'appliesToOrder',
                                            e.target.checked
                                        )
                                    }
                                />
                                <span className="label-text">Procedimentos</span>
                            </label>

                            <label className="label cursor-pointer justify-start gap-3 p-3 rounded-lg bg-base-200/50 hover:bg-base-200">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.appliesToHospitalization}
                                    onChange={e =>
                                        updateFormField(
                                            'appliesToHospitalization',
                                            e.target.checked
                                        )
                                    }
                                />
                                <span className="label-text">Internações</span>
                            </label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Códigos de Procedimento (opcional)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: 10101012, 10101020"
                                    value={formData.procedureCodes}
                                    onChange={e =>
                                        updateFormField(
                                            'procedureCodes',
                                            e.target.value
                                        )
                                    }
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Separe os códigos por vírgula
                                    </span>
                                </label>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Categorias (opcional)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: LABORATÓRIO, IMAGEM"
                                    value={formData.orderCategories}
                                    onChange={e =>
                                        updateFormField(
                                            'orderCategories',
                                            e.target.value
                                        )
                                    }
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Separe as categorias por vírgula
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Validity */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Vigência</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data Início
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.validFrom}
                                    onChange={e =>
                                        updateFormField(
                                            'validFrom',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data Fim
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.validUntil}
                                    onChange={e =>
                                        updateFormField(
                                            'validUntil',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                        <label className="label">
                            <span className="label-text-alt text-base-content/60">
                                Deixe em branco para vigência indeterminada
                            </span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/billing/coparticipação')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
